/**
 *      ▄ █
 *    █ █ ▀▀█  LOADBALANCER CLIENT
 *    █ ▀ ▀ █  @copyright Copyright (c) 2025
 */
import { ApiService, AuthStoreService } from '../../../../core/services';
import { ErrorResponseModel } from '../../../../core/models';
import { Injectable } from '@angular/core';
import { ModulesEnum } from '../../../../core/enums';
import { Observable, Subscriber } from 'rxjs';
import { PortalRbacApiEnum } from '../enums/api';

@Injectable()
export class PortalRbacApiService {
    constructor(
        private apiService: ApiService,
        private authStoreService: AuthStoreService
    ) {
    }

    /**
     * Get all paths
     * @param options
     */
    public getUserEndpoints(options?: any): Observable<any> {
        return new Observable((observer: Subscriber<any>): void => {
            // Get our selected module details
            const moduleAuth = options?.auth || this.authStoreService.getSelectedModuleAuth(ModulesEnum.CENTRALISEDMANAGEMENT);
            this.apiService.get(PortalRbacApiEnum.GET_ROUTES_CURRENT_USER, moduleAuth)
                .subscribe({
                    next: (data: any): void => {
                        observer.next(data);
                        observer.complete();
                    },
                    error: (error: ErrorResponseModel): void => {
                        observer.error(error);
                        observer.complete();
                    }
                });
        });
    }

    /**
     * Get all paths
     * @param options
     */
    public getAvailableEndpoints(options?: any): Observable<any> {
        return new Observable((observer: Subscriber<any>): void => {
            // Get our selected module details
            const moduleAuth = options?.auth || this.authStoreService.getSelectedModuleAuth(ModulesEnum.CENTRALISEDMANAGEMENT);
            this.apiService.get(PortalRbacApiEnum.GET_ROUTES_ENDPOINTS, moduleAuth)
                .subscribe({
                    next: (data: any): void => {
                        observer.next(data);
                        observer.complete();
                    },
                    error: (error: ErrorResponseModel): void => {
                        observer.error(error);
                        observer.complete();
                    }
                });
        });
    }

    /**
     * Create a role for an organisation
     * @param role
     * @param options
     */
    public createRole(role: any, options?: any): Observable<any> {
        return new Observable((observer: Subscriber<any>): void => {
            // Get our selected module details
            const moduleAuth = options?.auth || this.authStoreService.getSelectedModuleAuth(ModulesEnum.CENTRALISEDMANAGEMENT);

            this.apiService.post(PortalRbacApiEnum.POST_RBAC_ROLE, role, moduleAuth)
                .subscribe({
                    next: (data: any): void => {
                        observer.next(data);
                        observer.complete();
                    },
                    error: (error: ErrorResponseModel): void => {
                        observer.error(error);
                        observer.complete();
                    }
                });
        });
    }

    /**
     * Get roles for an organisation.
     * @param options
     */
    public getRoles(options?: any): Observable<any> {
        return new Observable((observer: Subscriber<any>): void => {
            // Get our selected module details
            const moduleAuth = options?.auth || this.authStoreService.getSelectedModuleAuth(ModulesEnum.CENTRALISEDMANAGEMENT);

            this.apiService.get(PortalRbacApiEnum.GET_RBAC_ROLES, moduleAuth)
                .subscribe({
                    next: (data: any): void => {
                        observer.next(data);
                        observer.complete();
                    },
                    error: (error: ErrorResponseModel): void => {
                        observer.error(error);
                        observer.complete();
                    }
                });
        });
    }

    /**
     * Get role by ID.
     * @param id
     * @param options
     */
    public getRole(id: string, options?: any): Observable<any> {
        return new Observable((observer: Subscriber<any>): void => {
            // Get our selected module details
            const moduleAuth = options?.auth || this.authStoreService.getSelectedModuleAuth(ModulesEnum.CENTRALISEDMANAGEMENT);

            this.apiService.get(PortalRbacApiEnum.GET_RBAC_ROLE(id), moduleAuth)
                .subscribe({
                    next: (data: any): void => {
                        observer.next(data);
                        observer.complete();
                    },
                    error: (error: ErrorResponseModel): void => {
                        observer.error(error);
                        observer.complete();
                    }
                });
        });
    }

    /**
     * Edit role.
     * @param id
     * @param data
     * @param options
     */
    public editRole(id: string, data: any, options?: any): Observable<any> {
        return new Observable((observer: Subscriber<any>): void => {
            // Get our selected module details
            const moduleAuth = options?.auth || this.authStoreService.getSelectedModuleAuth(ModulesEnum.CENTRALISEDMANAGEMENT);

            this.apiService.patch(PortalRbacApiEnum.PATCH_RBAC_ROLE(id), data, moduleAuth)
                .subscribe({
                    next: (data: any): void => {
                        observer.next(data);
                        observer.complete();
                    },
                    error: (error: ErrorResponseModel): void => {
                        observer.error(error);
                        observer.complete();
                    }
                });
        });
    }

    /**
     * Delete role.
     * @param id
     * @param options
     */
    public deleteRole(id: string, options?: any): Observable<any> {
        return new Observable((observer: Subscriber<any>): void => {
            // Get our selected module details
            const moduleAuth = options?.auth || this.authStoreService.getSelectedModuleAuth(ModulesEnum.CENTRALISEDMANAGEMENT);

            this.apiService.delete(PortalRbacApiEnum.DELETE_RBAC_ROLE(id), moduleAuth)
                .subscribe({
                    next: (data: any): void => {
                        observer.next(data);
                        observer.complete();
                    },
                    error: (error: ErrorResponseModel): void => {
                        observer.error(error);
                        observer.complete();
                    }
                });
        });
    }

    /**
     * Create a role for an organisation
     * @param role
     * @param options
     */
    public createGroup(role: any, options?: any): Observable<any> {
        return new Observable((observer: Subscriber<any>): void => {
            // Get our selected module details
            const moduleAuth = options?.auth || this.authStoreService.getSelectedModuleAuth(ModulesEnum.CENTRALISEDMANAGEMENT);

            this.apiService.post(PortalRbacApiEnum.POST_RBAC_GROUP, role, moduleAuth)
                .subscribe({
                    next: (data: any): void => {
                        observer.next(data);
                        observer.complete();
                    },
                    error: (error: ErrorResponseModel): void => {
                        observer.error(error);
                        observer.complete();
                    }
                });
        });
    }

    /**
     * Get groups.
     * @param options
     */
    public getGroups(options?: any): Observable<any> {
        return new Observable((observer: Subscriber<any>): void => {
            // Get our selected module details
            const moduleAuth = options?.auth || this.authStoreService.getSelectedModuleAuth(ModulesEnum.CENTRALISEDMANAGEMENT);

            this.apiService.get(PortalRbacApiEnum.GET_RBAC_GROUPS, moduleAuth)
                .subscribe({
                    next: (data: any): void => {
                        observer.next(data);
                        observer.complete();
                    },
                    error: (error: ErrorResponseModel): void => {
                        observer.error(error);
                        observer.complete();
                    }
                });
        });
    }

    /**
     * Get group by ID.
     * @param id
     * @param options
     */
    public getGroup(id: string, options?: any): Observable<any> {
        return new Observable((observer: Subscriber<any>): void => {
            // Get our selected module details
            const moduleAuth = options?.auth || this.authStoreService.getSelectedModuleAuth(ModulesEnum.CENTRALISEDMANAGEMENT);

            this.apiService.get(PortalRbacApiEnum.GET_RBAC_GROUP(id), moduleAuth)
                .subscribe({
                    next: (data: any): void => {
                        observer.next(data);
                        observer.complete();
                    },
                    error: (error: ErrorResponseModel): void => {
                        observer.error(error);
                        observer.complete();
                    }
                });
        });
    }

    /**
     * Edit group.
     * @param id
     * @param data
     * @param options
     */
    public editGroup(id: string, data: any, options?: any): Observable<any> {
        return new Observable((observer: Subscriber<any>): void => {
            // Get our selected module details
            const moduleAuth = options?.auth || this.authStoreService.getSelectedModuleAuth(ModulesEnum.CENTRALISEDMANAGEMENT);

            this.apiService.patch(PortalRbacApiEnum.PATCH_RBAC_GROUP(id), data, moduleAuth)
                .subscribe({
                    next: (data: any): void => {
                        observer.next(data);
                        observer.complete();
                    },
                    error: (error: ErrorResponseModel): void => {
                        observer.error(error);
                        observer.complete();
                    }
                });
        });
    }

    /**
     * Delete group.
     * @param id
     * @param options
     */
    public deleteGroup(id: string, options?: any): Observable<any> {
        return new Observable((observer: Subscriber<any>): void => {
            // Get our selected module details
            const moduleAuth = options?.auth || this.authStoreService.getSelectedModuleAuth(ModulesEnum.CENTRALISEDMANAGEMENT);

            this.apiService.delete(PortalRbacApiEnum.DELETE_RBAC_GROUP(id), moduleAuth)
                .subscribe({
                    next: (data: any): void => {
                        observer.next(data);
                        observer.complete();
                    },
                    error: (error: ErrorResponseModel): void => {
                        observer.error(error);
                        observer.complete();
                    }
                });
        });
    }

    public addUserToRole(id: string, data: any, options?: any): Observable<any> {
        return new Observable((observer: Subscriber<any>): void => {
            // Get our selected module details
            const moduleAuth = options?.auth || this.authStoreService.getSelectedModuleAuth(ModulesEnum.CENTRALISEDMANAGEMENT);

            this.apiService.patch(
                PortalRbacApiEnum.PATCH_USER_TO_ROLE(id),
                data,
                moduleAuth
            ).subscribe({
                next: (data: any): void => {
                    observer.next(data);
                    observer.complete();
                },
                error: (error: ErrorResponseModel): void => {
                    observer.error(error);
                    observer.complete();
                }
            });
        });
    }
}
